import {
  ShiftManagementAreasViewPageDocument,
  ShiftManagementBulkCreateShiftsDocument,
  ShiftManagementMainPageDocument,
  ShiftManagementShiftDropDocument,
} from 'generated/resources/graphql'
import type moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { buildAddShiftsInputParameters } from 'modules/shift_management/lib/buildAddShiftsInput'
import {
  trackAssignEmployeesUnassignedShiftSubmit,
  trackEditShiftSubmit,
} from 'modules/shift_management/lib/trackEvents'
import { Shift, ShiftsRow, UnassignedShift } from 'modules/shift_management/lib/types'
import ShiftModel, { NO_WORK_AREA } from 'modules/shift_management/store/internal/models/Shift'

export interface MoveShiftProps {
  originShiftsRow?: ShiftsRow
  newShiftsRow: ShiftsRow
  shift?: Shift
  unassignedShift?: UnassignedShift
  date: moment.Moment
  daysByEmployee: Record<string, string[]>
  employeeId: number
}

const useMoveShift = () => {
  const [{ view }] = useSchedule()
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()

  const dispatchSelection = useSelectionDispatch()
  const { refreshStats } = useRefreshStats()

  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const [dropShift] = useMutation(ShiftManagementShiftDropDocument, {
    extractorFn: (data) => data.shiftManagementMutations.dropShiftmanagementShift,
  })

  const [bulkCreate] = useMutation(ShiftManagementBulkCreateShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementShift,
  })

  const moveShift = async ({
    newShiftsRow,
    shift,
    unassignedShift,
    date,
    daysByEmployee,
    employeeId,
  }: MoveShiftProps) => {
    const isDaily = view.isDaily
    const isArea = view.isByAreas
    const [locationId, workAreaId] = newShiftsRow.key.split('-')
    const formattedDate = dateToString(date, 'backend')
    const formattedTime = dateToString(date, 'ui_time')

    if (unassignedShift) {
      const shifts = buildAddShiftsInputParameters({
        view,
        hasSelection: true,
        defaultAttributesByDate: {},
        name: null,
        unparsedStartTime: unassignedShift.startTime,
        unparsedEndTime: unassignedShift.endTime,
        daysByRow: daysByEmployee,
        defaultShiftId: unassignedShift.defaultShiftId,
        extraHours: unassignedShift.extraHours,
        notes: unassignedShift.notes,
        locationId: unassignedShift.locationId,
        workAreaId: unassignedShift.workAreaId,
        color: null,
        templateWeekId: null,
        state: 'draft',
        unassignedShiftId: unassignedShift.id,
        leaveId: undefined,
        companyId: currentCompanyId,
      })
      const result = await bulkCreate({
        variables: { shifts },
        // TODO: Actually update the cache properly
        refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
      })

      const handled = handleResult(result, (_errors) => {})

      if (handled.success) {
        const shiftsData = handled.data?.shifts
        if (shiftsData) {
          const newShifts = shiftsData.map((newShift) =>
            ShiftModel.buildFromGraphQL({
              shift: newShift,
              employeeFullName: newShift.employee?.fullName || '',
              employeeAvatar: newShift.employee?.avatar?.url || '',
              employeeLocationId: newShift.employee?.locationId || 0,
            })
          )
          dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: newShifts, view })
          refreshStats(shiftsRows)
        }

        trackAssignEmployeesUnassignedShiftSubmit(unassignedShift.counter, 1, 'drag-and-drop')
      }
    } else if (shift) {
      const result = await dropShift({
        variables: {
          id: shift.id,
          employeeId,
          companyId: currentCompanyId,
          date: isDaily ? null : formattedDate,
          startTime: isDaily ? formattedTime : null,
          locationId: isArea ? Number(locationId) : null,
          workAreaId: isArea && workAreaId !== NO_WORK_AREA ? Number(workAreaId) : null,
          dropAction: 'move',
        },
        // TODO: Actually update the cache properly
        refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
      })

      const handled = handleResult(result, (_errors) => {})

      if (handled.success) {
        const shiftData = handled.data?.shift
        if (shiftData) {
          const newShift = ShiftModel.buildFromGraphQL({
            shift: shiftData,
            employeeFullName: shiftData.employee?.fullName || '',
            employeeAvatar: shiftData.employee?.avatar?.url || '',
            employeeLocationId: shiftData.employee?.locationId || 0,
          })

          dispatchShiftsRows({ type: 'DESTROY_SHIFT', shift, view })
          dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: [newShift], view })
          dispatchSelection({ type: 'CLEAR_SELECTION' })
        }

        refreshStats(shiftsRows)

        await trackEditShiftSubmit({
          shifts: [shift],
          name: shift.name,
          startAt: null,
          endAt: null,
          notes: null,
          extraHours: null,
          locationId: isArea ? Number(locationId) : null,
          workAreaId: isArea ? Number(workAreaId) : null,
          mixedValueNotes: 0,
          view,
          assignedToModified: false,
          originalEmployeeIncluded: false,
          totalNumberOfAssignedEmployees: 1,
          through: 'drag-and-drop',
          date: isDaily ? formattedTime : formattedDate,
        })
      }
    }
  }

  return { moveShift }
}

export default useMoveShift
